import React, { useCallback, useEffect, useState } from 'react';
import platformApi, { platformErrorMessage } from '../services/platformApi';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import { formatInr, formatMonth, formatDate } from '../utils/billingAdmin';

const MODES = [
  { value: 'all', label: 'All payments' },
  { value: 'live', label: 'Live only' },
  { value: 'test', label: 'Test only' },
];

function Kpi({ label, value, hint }) {
  return (
    <div className="stat-card" style={{ flexDirection: 'column', gap: '0.25rem' }}>
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value" style={{ fontSize: '1.5rem' }}>{value}</div>
      {hint && <div className="form-hint">{hint}</div>}
    </div>
  );
}

function change(current, previous) {
  if (!previous) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  return `${pct >= 0 ? '+' : ''}${pct}% vs last month`;
}

function Revenue() {
  const [mode, setMode] = useState('all');
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await platformApi.get('/platform/revenue', { params: { mode, months } });
      setData(response.data?.data || null);
    } catch (err) {
      setError(platformErrorMessage(err, 'Failed to load revenue'));
    } finally {
      setLoading(false);
    }
  }, [mode, months]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <LoadingScreen message="Loading revenue..." />;

  const thisMonth = data?.thisMonth || { gross: 0, refunds: 0, net: 0, payments: 0 };
  const lastMonth = data?.lastMonth || { gross: 0, refunds: 0, net: 0, payments: 0 };
  const maxNet = Math.max(1, ...(data?.series || []).map((m) => Math.max(m.gross, 0)));

  return (
    <div className="page">
      <PageHeader
        title="Revenue"
        description="Money collected through checkout (including GST), refunds, and recurring revenue from active paid plans (plan prices before GST). Months follow IST."
        action={(
          <div className="btn-group">
            <select className="form-select" value={mode} onChange={(e) => setMode(e.target.value)} aria-label="Payment mode">
              {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <select className="form-select" value={months} onChange={(e) => setMonths(Number(e.target.value))} aria-label="Months">
              {[3, 6, 12, 24].map((n) => <option key={n} value={n}>Last {n} months</option>)}
            </select>
          </div>
        )}
      />

      {error && <div className="alert alert--error">{error}</div>}

      {data && (
        <>
          <div className="stats-grid">
            <Kpi label="Net this month" value={formatInr(thisMonth.net, { decimals: false })} hint={change(thisMonth.net, lastMonth.net) || `${thisMonth.payments} payments`} />
            <Kpi label="Collected this month" value={formatInr(thisMonth.gross, { decimals: false })} hint={`${formatInr(thisMonth.refunds, { decimals: false })} refunded`} />
            <Kpi label="Net last month" value={formatInr(lastMonth.net, { decimals: false })} hint={`${lastMonth.payments} payments`} />
            <Kpi label="MRR" value={formatInr(data.mrr, { decimals: false })} hint={`ARR ${formatInr(data.arr, { decimals: false })}`} />
            <Kpi label="Paying workspaces" value={data.payingTenants} hint={`${data.totalSubscriptions} subscriptions in total`} />
            <Kpi label="MRR scheduled to cancel" value={formatInr(data.mrrScheduledToCancel, { decimals: false })} hint="Cancels at period end" />
            <Kpi label="Renewals due in 7 days" value={data.renewalsDueIn7Days} hint="Paid plans ending soon (no autopay)" />
            <Kpi label="Trials ending in 7 days" value={data.trialsEndingIn7Days} />
          </div>

          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card__body">
              <h3 style={{ marginTop: 0 }}>By month</h3>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr><th>Month</th><th>Collected</th><th>Refunds</th><th>Net</th><th>Promo discounts</th><th>Payments</th><th>Paying workspaces</th><th aria-label="Trend" /></tr>
                  </thead>
                  <tbody>
                    {[...data.series].reverse().map((m) => (
                      <tr key={m.month}>
                        <td>{formatMonth(m.month)}</td>
                        <td>{formatInr(m.gross)}</td>
                        <td>{m.refunds ? `−${formatInr(m.refunds)}` : '—'}</td>
                        <td><strong>{formatInr(m.net)}</strong></td>
                        <td>{m.discounts ? `${formatInr(m.discounts)} (${m.promoPayments})` : '—'}</td>
                        <td>{m.payments}</td>
                        <td>{m.payingTenants}</td>
                        <td style={{ width: '25%' }}>
                          <div style={{ background: 'var(--color-primary-light)', borderRadius: 4, height: 8, width: `${Math.max(0, (m.gross / maxNet) * 100)}%` }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
            <div className="card">
              <div className="card__body">
                <h3 style={{ marginTop: 0 }}>By plan</h3>
                <table className="data-table">
                  <thead><tr><th>Plan</th><th>Active</th><th>Trial</th><th>MRR</th></tr></thead>
                  <tbody>
                    {data.byPlan.map((p) => (
                      <tr key={p.planCode}>
                        <td>{p.planName}</td>
                        <td>{p.active}</td>
                        <td>{p.trial}</td>
                        <td>{formatInr(p.mrr, { decimals: false })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card">
              <div className="card__body">
                <h3 style={{ marginTop: 0 }}>Subscriptions by status</h3>
                <table className="data-table">
                  <tbody>
                    {Object.entries(data.statusCounts).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                      <tr key={status}><td>{status}</td><td>{count}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <p className="form-hint" style={{ marginTop: '1rem' }}>
            Updated {formatDate(data.generatedAt, { withTime: true })}. MRR counts active paid plans at list price before GST (yearly ÷ 12); partner-billed workspaces are included.
          </p>
        </>
      )}
    </div>
  );
}

export default Revenue;
